import { Component, OnInit, inject, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { FormsModule, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ContactService, ContactFormData } from '../core/services/contact.service';
import { SeoService } from '../core/services/seo.service';
import { NavigationComponent } from '../shared/navigation.component';
import { FooterComponent } from '../shared/footer.component';

@Component({
  selector: 'app-partners',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, NavigationComponent, FooterComponent],
  templateUrl: './partners.component.html',
  styleUrls: ['./partners.component.css']
})
export class PartnersComponent implements OnInit {
  private fb = inject(FormBuilder);
  private contactService = inject(ContactService);
  private seoService = inject(SeoService);
  private document = inject(DOCUMENT);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  // Form usando ReactiveFormsModule
  partnerForm!: FormGroup;
  isSubmitting = false;
  submitMessage = '';
  submitSuccess = false;

  // Tabla de planes con comisiones
  readonly plans = [
    { name: 'Basic', price: 999, commission25: 250, commission10: 100 },
    { name: 'Professional', price: 2999, commission25: 750, commission10: 300 },
    { name: 'Enterprise', price: 9999, commission25: 2500, commission10: 1000 }
  ];

  // FAQ data
  readonly faqs = [
    {
      question: '¿Necesito experiencia vendiendo software?',
      answer: 'No es necesario. Te capacitamos en nuestro producto, te damos materiales de venta y soporte continuo. Solo necesitas ganas de vender y contactos potenciales.'
    },
    {
      question: '¿Hay un límite de clientes que puedo referir?',
      answer: 'No hay límite. Puedes referir todos los clientes que desees y ganarás comisión por cada uno que se suscriba y permanezca activo.'
    },
    {
      question: '¿Cómo se trackean mis ventas?',
      answer: 'Cada comisionista recibe un código de referencia único. Cuando tu cliente se registra con tu código, el sistema automáticamente asigna la venta a ti y calcula tus comisiones.'
    },
    {
      question: '¿Qué materiales me proporcionan?',
      answer: 'Recibes presentaciones de ventas, videos demostrativos, casos de éxito, argumentarios de venta, material gráfico, y acceso a una cuenta de prueba para demos en vivo.'
    },
    {
      question: '¿Qué pasa si mi cliente cancela después de 3 meses?',
      answer: 'No hay penalización. Ya cobraste el 25% del primer mes y el 10% de los meses que el cliente estuvo activo. Simplemente dejas de recibir la comisión recurrente del cliente cancelado.'
    },
    {
      question: '¿Cómo y cuándo recibo mis pagos?',
      answer: 'Los pagos se realizan mensualmente el día 15 mediante transferencia bancaria. Recibes un reporte detallado con todas tus comisiones del mes anterior.'
    }
  ];

  ngOnInit() {
    this.setupSEO();
    this.initializeForm();

    // Scroll to top
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
  }

  private initializeForm() {
    this.partnerForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern(/^[\d\s\-\+\(\)]+$/)]],
      experience: ['', [Validators.required]],
      strategy: ['', [Validators.required, Validators.minLength(20)]],
      terms: [false, [Validators.requiredTrue]]
    });
  }

  private setupSEO() {
    this.seoService.updateSEOData({
      title: 'Programa de Comisionistas | Gana Dinero con Chatbot Pezeta',
      description: 'Únete a nuestro programa de comisionistas. 25% del primer mes + 10% recurrente. Sin inversión inicial ni cuotas mínimas. Pagos puntuales mensuales.',
      keywords: 'comisionistas chatbot, programa afiliados mexico, vender chatbot whatsapp, comisiones software, reventa chatbot',
      ogTitle: 'Gana Dinero Vendiendo Chatbot Pezeta | 25% + 10% Recurrente',
      ogDescription: 'Programa de comisionistas sin inversión. Gana hasta $2,500 por venta + 10% mensual recurrente. Soporte completo para ventas.',
      ogImage: 'https://chatbot.pezetasolutions.com/images/og-image.webp',
      ogUrl: 'https://chatbot.pezetasolutions.com/partners',
      twitterTitle: 'Programa de Comisionistas | Chatbot Pezeta',
      twitterDescription: '25% primer mes + 10% recurrente. Sin inversión. Producto probado. Pagos puntuales.',
      twitterImage: 'https://chatbot.pezetasolutions.com/images/og-image.webp',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        "title": "Comisionista de Ventas - Chatbot Pezeta",
        "description": "Programa de comisionistas para venta de chatbot WhatsApp con IA. 25% comisión primer mes + 10% recurrente mensual.",
        "hiringOrganization": {
          "@type": "Organization",
          "name": "Pezeta Solutions",
          "url": "https://chatbot.pezetasolutions.com"
        },
        "employmentType": "CONTRACTOR",
        "baseSalary": {
          "@type": "MonetaryAmount",
          "currency": "MXN",
          "value": {
            "@type": "QuantitativeValue",
            "minValue": "250",
            "maxValue": "2500",
            "unitText": "MONTH"
          }
        }
      }
    });
  }

  onSubmit() {
    if (this.partnerForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.submitMessage = '';

      // Preparar datos con campos específicos para registro de partners
      const formData: ContactFormData = {
        fullName: this.partnerForm.get('name')?.value,
        email: this.partnerForm.get('email')?.value,
        phoneNumber: this.partnerForm.get('phone')?.value,
        serviceType: 'partner-registration', // Tipo específico para comisionistas
        budgetRange: 'N/A',
        projectDeadline: 'N/A',
        message: `REGISTRO PROGRAMA COMISIONISTAS

Experiencia vendiendo software: ${this.partnerForm.get('experience')?.value}

Estrategia para conseguir clientes:
${this.partnerForm.get('strategy')?.value}

Acepta términos: ${this.partnerForm.get('terms')?.value ? 'Sí' : 'No'}`
      };

      this.contactService.sendContactForm(formData).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.submitSuccess = true;
          this.submitMessage = '¡Gracias por tu interés! Hemos recibido tu solicitud. Te contactaremos en las próximas 24 horas para iniciar tu proceso de registro como comisionista.';
          this.partnerForm.reset();

          // Scroll to message only in browser
          if (isPlatformBrowser(this.platformId)) {
            setTimeout(() => {
              const messageElement = this.document.querySelector('.submit-message');
              if (messageElement) {
                messageElement.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          this.submitSuccess = false;
          this.submitMessage = error.message || 'Error al enviar el formulario. Por favor, intenta nuevamente.';

          // Scroll to message only in browser
          if (isPlatformBrowser(this.platformId)) {
            setTimeout(() => {
              const messageElement = this.document.querySelector('.submit-message');
              if (messageElement) {
                messageElement.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          }
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched() {
    Object.keys(this.partnerForm.controls).forEach(key => {
      const control = this.partnerForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.partnerForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.partnerForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) {
        return 'Este campo es obligatorio';
      }
      if (field.errors['email']) {
        return 'Ingresa un email válido';
      }
      if (field.errors['minlength']) {
        return `Mínimo ${field.errors['minlength'].requiredLength} caracteres`;
      }
      if (field.errors['pattern']) {
        return 'Formato de teléfono inválido';
      }
    }
    return '';
  }

  scrollToForm() {
    if (isPlatformBrowser(this.platformId)) {
      const element = this.document.getElementById('partner-form');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  openWhatsApp() {
    if (isPlatformBrowser(this.platformId)) {
      window.open(`https://wa.me/message/GTV3AEMP6MFNC1`, '_blank');
    }
  }
}
